import { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    FormGroup,
    Alert,
    AlertColor,
    AlertPropsVariantOverrides,
    FormControlLabel,
    FormLabel,
    Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axiosInstance from "../../services/axios-instance";
import { ROLES } from "../../assets/data/roles";
import { ADD_USER } from "../../services/endpoints";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AllUsersInterface } from "../../interface/allUsersInterface";
import { OverridableStringUnion } from "@mui/types";
import axios from "axios";
import { userLevels } from "../../interface/userLevels";
import { userAdditionalRoles } from "../../interface/userAdditionalRoles";

const StyledForm = styled("form")({
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
});

const roles = Object.keys(ROLES)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
        value: key,
        key: ROLES[key as keyof typeof ROLES],
    }));

const validationSchema = Yup.object({
    name: Yup.string().required("User Name is required"),
    email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
    role_id: Yup.string().required("User Role is required"),
    password: Yup.string()
        .required("Password is required")
        .min(6, "Password must be at least 6 characters"),
    password_confirmation: Yup.string()
        .required("Confirm Password is required")
        .oneOf([Yup.ref("password")], "Passwords must match"),
});


interface AddUserFormParams {
    onAddUser: (newUser: Partial<AllUsersInterface>) => void;
}

interface Client {
    id: number;
    name: string;
}

function AddUserForm({ onAddUser }: AddUserFormParams) {
    const [alertMessage, setAlertMessage] = useState("");
    const [clients, setClients] = useState<Client[]>([]);    
    const [severity, setSeverity] = useState<AlertColor | undefined>("info");
    const [variant, setVariant] = useState<
        | OverridableStringUnion<
              "standard" | "filled" | "outlined",
              AlertPropsVariantOverrides
          >
        | undefined
    >("filled");

    useEffect(() => {
        fetchClients();
    }, []);
    
    const fetchClients = async () => {
        try {
            const response = await axiosInstance.get("/clients");
            setClients(response.data.data);
        } catch (error) {
            console.error("Error fetching clients:", error);
        }
    };

    const formik = useFormik({
        initialValues: {
            name: "",
            email: "",
            role_id: roles[2].key,
            password: "",
            password_confirmation: "",
            level: "",
            additional_roles: "",
            client_id: 0,
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { resetForm }) => {
            try {
                const response = await axiosInstance.post(ADD_USER, values);

                if (response.status === 200 || response.status === 201) {
                    // Handle success scenario
                    setSeverity("success");
                    setVariant("filled");
                    setAlertMessage("User added successfully");

                    onAddUser(response.data.data as AllUsersInterface);
                    resetForm(); // Reset the form after successful submission
                } else {
                    setSeverity("error");
                    setAlertMessage("Error submitting form");
                }
            } catch (error) {
                if (axios.isAxiosError(error)) {
                    setSeverity("error");
                    const message = Object.entries(
                        error.response?.data.message
                    )[0][1];
                    if (typeof message === "object") {
                        setAlertMessage("Error: " + message);
                    }
                }
            }
        },
    });

    return (
        <>
            <Box maxWidth="500px" mx="auto">
                <StyledForm onSubmit={formik.handleSubmit} noValidate>
                    <FormGroup>
                        <TextField
                            name="name"
                            label="User Name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.name &&
                                Boolean(formik.errors.name)
                            }
                            required
                        />
                        {formik.errors.name && formik.touched.name ? (
                            <FormHelperText>
                                {formik.errors.name}
                            </FormHelperText>
                        ) : null}
                    </FormGroup>
                    <FormGroup>
                        <TextField
                            name="email"
                            label="User Email"
                            type="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.email &&
                                Boolean(formik.errors.email)
                            }
                            required
                        />
                        {formik.errors.email && formik.touched.email ? (
                            <FormHelperText>
                                {formik.errors.email}
                            </FormHelperText>
                        ) : null}
                    </FormGroup>
                    <FormGroup>
                        <TextField
                            name="password"
                            label="Password"
                            type="password"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.password &&
                                Boolean(formik.errors.password)
                            }
                            required
                        />
                        {formik.errors.password && formik.touched.password ? (
                            <FormHelperText>
                                {formik.errors.password}
                            </FormHelperText>
                        ) : null}
                    </FormGroup>
                    <FormGroup>
                        <TextField
                            name="password_confirmation"
                            label="Confirm Password"
                            type="password"
                            value={formik.values.password_confirmation}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.password_confirmation &&
                                Boolean(formik.errors.password_confirmation)
                            }
                            required
                        />
                        {formik.errors.password_confirmation &&
                        formik.touched.password_confirmation ? (
                            <FormHelperText>
                                {formik.errors.password_confirmation}
                            </FormHelperText>
                        ) : null}
                    </FormGroup>
                    <FormControl required>
                        <InputLabel>User Role </InputLabel>

                        <Select
                            name="role_id"
                            value={formik.values.role_id}
                            onChange={formik.handleChange}
                            label="User Role"
                        >
                            {roles.map((role) => (
                                <MenuItem key={role.key} value={role.key}>
                                    {role.value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {formik.values.role_id && formik.values.role_id != 3 ? (
                    <>
                    <FormControl>
                        <InputLabel>User Level </InputLabel>

                        <Select
                            name="level"
                            value={formik.values.level}
                            onChange={formik.handleChange}
                            label="User Level"
                        >
                            {userLevels.map((level) => (
                                <MenuItem key={level} value={level}>
                                    {level}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Additional Roles</FormLabel>
                        <FormGroup>
                            {userAdditionalRoles.map((role) => (
                                <FormControlLabel
                                    key={role.key}
                                    control={
                                        <Checkbox
                                            checked={formik.values.additional_roles.includes(
                                                role.key
                                            )}
                                            onChange={formik.handleChange}
                                            name="additional_roles"
                                            value={role.key}
                                        />
                                    }
                                    label={role.label}
                                />
                            ))}
                        </FormGroup>
                    </FormControl>
                    </>
                    ) :
                    (
                    <FormControl>
                        <InputLabel>Client </InputLabel>
                        <Select
                            name="client_id"
                            value={formik.values.client_id}
                            onChange={formik.handleChange}
                            label="Client"
                        >
                            {clients.map((client) => (
                                <MenuItem key={client.id} value={client.id}>
                                    {client.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    )
                    }

                    <Button
                        type="submit"
                        variant="contained"
                        color="info"
                        disabled={formik.isSubmitting || !formik.isValid}
                    >
                        {formik.isSubmitting ? "Adding User..." : "Add User"}
                    </Button>
                </StyledForm>
                <Box sx={{ mt: "1rem" }}>
                    {alertMessage != "" && (
                        <Alert
                            onClick={() => setAlertMessage("")}
                            variant={variant}
                            severity={severity}
                        >
                            {alertMessage}
                        </Alert>
                    )}
                </Box>
            </Box>
        </>
    );
}

export default AddUserForm;
